/**
 * "No more than this many at once."
 *
 * Some work is cheap to ask for and dear to do: shrinking a photograph means
 * decoding the whole 1600px original — ten megabytes of pixels apiece — and a
 * constellation that has just opened asks for two hundred of them in the same
 * breath. Let loose all at once that is gigabytes of bitmaps on a phone that
 * has three, and the tab is killed before the first circle is drawn. Through
 * here they go a few at a time and the rest wait their turn.
 *
 * **The newest batch first, and each batch in the order it was asked.**
 * Whatever was asked for last is what is on the screen *now* — the person has
 * scrolled, or panned, or opened something else — so a newer batch goes ahead of
 * an older one. But *within* a batch the order is kept, because a screen asks for
 * its pictures in the order that matters to it: a constellation asks from the
 * middle outwards, and the circles round the face should fill before the ones
 * at the edge. (Plain newest-first got that exactly backwards: the far side of
 * the ring filled in first and the middle last.) A batch is everything asked
 * for within a breath of each other. Nothing is ever dropped.
 */
const SAME_BATCH_MS = 80;

export function limiter(max: number): <T>(work: () => Promise<T>) => Promise<T> {
  let active = 0;
  const batches: Array<{ at: number; waiting: Array<() => void> }> = [];
  const next = () => {
    if (active >= max) return;
    while (batches.length && !batches[batches.length - 1].waiting.length) batches.pop();
    const start = batches[batches.length - 1]?.waiting.shift();
    if (start) start();
  };
  return <T>(work: () => Promise<T>) =>
    new Promise<T>((resolve, reject) => {
      const now = Date.now();
      let batch = batches[batches.length - 1];
      if (!batch || now - batch.at > SAME_BATCH_MS) {
        batch = { at: now, waiting: [] };
        batches.push(batch);
      }
      batch.waiting.push(() => {
        active++;
        work()
          .then(resolve, reject)
          .finally(() => {
            active--;
            next();
          });
      });
      next();
    });
}
